<template>
    <div class="user-management-form">
        <user-select-modal
            v-if="isPortalBase"
            :trans="trans"
            :action="actions.getMorePortalUsers"
            vuex-state="portalUsers"
        >
        </user-select-modal>

        <user-management-confirmation-modal
            v-on:confirmation-event="sendSaveRequest"
            :trans="trans"
        >
        </user-management-confirmation-modal>

        <user-limit-exceeded-modal
            :trans="trans"
        >
        </user-limit-exceeded-modal>

        <div class="attached-users">
            <h1 class="title is-5 mb-0">
                {{ trans.attached_users_title }}
            </h1>
            <p class="is-size-6 has-text-grey has-text-justified mb-1">
                {{ trans.attached_users_subtitle }}
            </p>
            <user-list
                vuex-state="attachedUsers"
                :allow-remove="!isSaved"
            >
            </user-list>
        </div>

        <div class="invited-users mt-1" v-if="pendingInvitesExists">
            <h1 class="title is-5 mb-0">
                {{ trans.invited_users_title }}
            </h1>
            <p class="is-size-6 has-text-grey has-text-justified mb-1">
                {{ trans.invited_users_subtitle }}
            </p>
            <user-list
                vuex-state="pendingInvites"
                :allow-remove="!isSaved"
            >
            </user-list>
        </div>

        <div v-if="isSaved" class="mt-1">
            <p class="has-text-weight-bold has-text-success">
                {{ trans.success_msg }}
            </p>
            <p class="has-text-success">
                {{ trans.success_redirect_msg }}
            </p>
        </div>
        <transition name="slide-fade">
            <div v-if="!isSaved">
                <div class="attaching-users mt-1" v-if="attachingUsersExist">
                    <h1 class="title is-5 mb-0">
                        {{ trans.will_be_attached_users_title }}
                    </h1>
                    <p class="is-size-6 has-text-grey has-text-justified mb-1">
                        {{ trans.will_be_attached_users_subtitle[context] }}
                    </p>
                    <userList
                        :allow-remove="true"
                        vuex-state="attachingUsers"
                    >
                    </userList>
                </div>

                <div class="detaching-users mt-1" v-if="detachingUsersExists">
                    <h1 class="title is-5 mb-0">
                        {{ trans.detaching_users_title }}
                    </h1>
                    <p class="is-size-6 has-text-grey has-text-justified mb-1">
                        {{ trans.detaching_users_subtitle }}
                    </p>
                    <user-list
                        vuex-state="detachingUsers"
                        :allow-remove="true"
                    >
                    </user-list>
                </div>

                <div class="user-invite mt-2" v-if="!isPortalBase">
                    <h1 class="title is-5 mb-1">
                        {{ trans.invite_users_title }}
                    </h1>

                    <user-invite-form
                        :trans="trans"
                        :action="actions.validateEmail"
                    >
                    </user-invite-form>
                </div>
            </div>
        </transition>

        <div class="button-container">
            <button
                v-if="isPortalBase"
                class="button is-success"
                @click="showUserSelectModal"
                :disabled="isSaved"
            >
                {{ trans.add_from_portal_button }}
            </button>

            <button
                class="button is-info"
                :class="{ 'is-loading': isLoading }"
                :disabled="isSaved || isLoading"
                @click="saveChanges"
            >
                {{ isFirstVisit ? trans.next_button : trans.confirm_button }}
            </button>
        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters, mapState} from "vuex";
    import UserSelectModal from "./UserSelectModal";
    import UserList from "./UserList";
    import {axiosForm} from "../../../mixins/axiosFormMixin";
    import UserManagementConfirmationModal from "./UserManagementConfirmationModal";
    import UserInviteForm from "./UserInviteForm";

    export default {
        mixins: [axiosForm],
        components: {
            UserInviteForm,
            UserManagementConfirmationModal,
            UserSelectModal,
            UserList,
        },
        props: {
            actions: Object || {},
            baseUsers: Object || {},
            pendingUsers: {
                type: [Object,  Array],
                default: {},
            },
            currentUser: String,
            trans: Object,
            successRoute: String,
            isPortalBase: Boolean,
            isFirstVisit: Boolean,
            context: String,
            maxUsers: {
                type: [Number, String],
            },
            ownerEmail: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                isSaved: false,
                okReport: 'Сохранение списка пользователей',
                failReport: 'Ошибка сохранения списка пользователей',
            };
        },
        mounted() {
            this.setTrans(this.trans);
            this.setAttachedUsers(this.baseUsers);
            this.setPendingInvites(this.pendingUsers);
            this.setOwnerUser(this.ownerEmail);
            this.setCurrentUser(this.currentUser);
            this.setUserLimit(this.maxUsers);
        },
        methods: {
            ...mapActions('userManagement', [
                'setTrans',
                'setAttachedUsers',
                'setPendingInvites',
                'showUserSelectModal',
                'showConfirmationModal',
                'setCurrentUser',
                'setOwnerUser',
                'setUserLimit',
                'clearAttachingUsers',
                'showUserLimitExceededModal',
            ]),

            /**
             * Обработчик нажатия кнопки «Сохранить»
             * @param e
             */
            saveChanges(e) {
                // Если это первый визит и не выбраны пользователи
                if (this.isFirstVisit && !this.attachingUsersExist) {
                    this.showConfirmationModal();
                    return;
                }

                this.sendSaveRequest();
            },

            /**
             * Отправка запроса на сохранение
             */
            sendSaveRequest() {
                let requestData = {
                    attaching: this.attachingUsers,
                    detaching: this.detachingUsers,
                };

                this.isLoading = true;

                this.sendRequest(this.action, requestData, 'post')
            },

            /**
             * Обработчик ответа сервера
             * @param response
             */
            postResultHandler(response) {
                if (response.data.synced) {
                    let timeoutValue = 0;

                    // Если были сохранены изменения - покажем сообщение и немного подождем
                    if (this.attachingUsersExist || this.detachingUsersExists) {
                        this.isSaved = true;
                        timeoutValue = 4000;

                        let updatedInvites = Object.assign(this.pendingInvites, this.attachingUsers);
                        this.clearAttachingUsers();
                        this.setPendingInvites(updatedInvites);
                    }

                    setTimeout(() => {
                        window.location.replace(this.successRoute);
                    }, timeoutValue);
                }
            },

            /**
             * Обработчик ошибок сервера
             * @param error
             */
            postErrorHandler(error) {
                if (error.response) {
                    let errorStatus = error.response.status;

                    if (errorStatus === 422) {
                        let errorList = error.response.data.errors;

                        if (Object.keys(errorList['attaching']).length) {
                            if (errorList['attaching'][0] == this.trans.user_limit_exceeded_modal_text) {
                                this.showUserLimitExceededModal();
                            }
                        }
                    }
                }
            }
        },
        computed: {
            ...mapGetters('userManagement', [
                'attachingUsersExist',
                'detachingUsersExists',
                'pendingInvitesExists',
            ]),
            ...mapState('userManagement', [
                'attachedUsers',
                'pendingInvites',
                'attachingUsers',
                'detachingUsers',
            ]),
        },
    };
</script>
